import { type RouteAssignment } from '@/api/route-plans';

// A located point on the map: a patient at a specific address. Coordinate equality is exact
// because the values come straight from the same DB record (the chosen PatientAddress).
export interface LocatedPoint {
  patientId: number | null;
  patientLatitude: number | null;
  patientLongitude: number | null;
}

export function locationKey(point: LocatedPoint): string {
  return `${point.patientId}:${point.patientLatitude}:${point.patientLongitude}`;
}

export interface AssignmentLocationGroup {
  key: string;
  first: RouteAssignment; // representative assignment (marker position, sequence, address)
  visits: RouteAssignment[]; // every visit at this location, in input order
}

// One group per (patient, coordinates): repeat visits to the SAME address share a group (and so a
// single marker), while distinct addresses of the same patient become separate groups. Assignments
// without coordinates are skipped (they can't be placed on the map).
export function groupAssignmentsByLocation(assignments: RouteAssignment[]): AssignmentLocationGroup[] {
  const groups = new Map<string, AssignmentLocationGroup>();

  for (const assignment of assignments) {
    if (assignment.patientLatitude === null || assignment.patientLongitude === null) continue;

    const key = locationKey(assignment);
    const group = groups.get(key);
    if (group) group.visits.push(assignment);
    else groups.set(key, { key, first: assignment, visits: [assignment] });
  }

  return [...groups.values()];
}
